import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { BaseCard, type BaseCardData } from './BaseCard';
import type {
  AnalyticsPeriod,
  AnalyticsMetric,
  MetricCardType,
} from '../const';
import { generateDynamicColors } from '../utils';
import { CHART_STYLES } from '../constants/chartStyles';
import ChartTooltip from './ChartTooltip';

export type BarChartData = BaseCardData & {
  data: Array<{
    name: string;
    value: number;
    timestamp?: string;
    metricType?: string;
  }>;
  type: AnalyticsMetric | MetricCardType;
  unit?: string | { minute: string; seconds: string };
  scale?: string | null;
  periodData?: {
    [key in AnalyticsPeriod]: Array<{
      name: string;
      value: number;
      timestamp?: string;
    }>;
  };
};

type BarChartCardProps = {
  item: BarChartData;
  period?: AnalyticsPeriod;
  onClick?: (item: BarChartData) => void;
  onChartClick?: (item: BarChartData) => void;
  height?: string;
  showCardTooltip?: boolean;
  hideTitle?: boolean;
  ticks?: number[] | undefined;
};

export const BarChartCard: React.FC<BarChartCardProps> = ({
  item,
  onClick,
  onChartClick,
  height = 'h-[280px]',
  showCardTooltip = true,
  hideTitle = false,
  ticks,
}) => {
  const chartData = item.data;

  const getLabelConfig = (dataLength: number) => {
    if (dataLength <= 7) {
      return {
        interval: 0,
        angle: 0,
        textAnchor: 'middle' as const,
        marginBottom: 15,
        fontSize: 12,
      };
    } else if (dataLength <= 15) {
      return {
        interval: 0,
        angle: -30,
        textAnchor: 'end' as const,
        marginBottom: 20,
        fontSize: 11,
      };
    } else {
      return {
        interval: 1,
        angle: -45,
        textAnchor: 'end' as const,
        marginBottom: 35,
        fontSize: 10,
      };
    }
  };

  const labelConfig = getLabelConfig(chartData.length);

  const formatTick = (value: string) => {
    if (item.scale === 'HOURS') {
      const match = value.match(/(\d{1,2}):\d{2}\s*(AM|PM)/i);
      if (match) {
        return `${match[1]} ${match[2]}`;
      }
      const hourMatch = value.match(/(\d{1,2})\s*(AM|PM)/i);
      if (hourMatch) {
        return `${hourMatch[1]} ${hourMatch[2]}`;
      }
    }
    return value;
  };

  const formatYAxisTick = (value: number) => {
    if (
      item.unit &&
      typeof item.unit === 'object' &&
      'minute' in item.unit &&
      'seconds' in item.unit
    ) {
      const minutes = Math.floor(value / 60);
      const seconds = Math.round(value % 60);
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    }
    return value.toString();
  };

  if (!chartData || chartData.length === 0) {
    return (
      <BaseCard<BarChartData>
        item={item}
        onClick={onClick}
        showChartButton={false}
        onChartClick={onChartClick}
        className={`w-full ${height} flex flex-col bg-white rounded-card cursor-pointer p-6 shadow-sm focus:outline-none focus-visible:outline-none`}
        headerClassName="flex justify-between items-start mb-4"
        showCardTooltip={showCardTooltip}
        hideTitle={hideTitle}
      >
        <div className="flex items-center justify-center h-full text-secondary">
          No data available
        </div>
      </BaseCard>
    );
  }

  const dynamicColors = generateDynamicColors(chartData);

  const dataWithColors = chartData.map((dataPoint, index) => ({
    ...dataPoint,
    fill: dynamicColors[index] || '#ece8ff',
  }));

  return (
    <BaseCard<BarChartData>
      item={item}
      onClick={onClick}
      showChartButton={false}
      onChartClick={onChartClick}
      className={`w-full ${height} flex flex-col bg-white rounded-card cursor-pointer p-6 shadow-sm focus:outline-none focus-visible:outline-none`}
      headerClassName="flex justify-between items-start mb-4"
      showCardTooltip={showCardTooltip}
      hideTitle={hideTitle}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dataWithColors}
          margin={{
            top: 10,
            right: 20,
            left: 0,
            bottom: labelConfig.marginBottom,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_STYLES.COLORS.GRID}
          />
          <XAxis
            dataKey="name"
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={labelConfig.fontSize}
            tickLine={false}
            axisLine={false}
            interval={labelConfig.interval}
            angle={labelConfig.angle}
            textAnchor={labelConfig.textAnchor}
            tick={{ fontSize: labelConfig.fontSize }}
            tickFormatter={formatTick}
          />
          <YAxis
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            tickFormatter={formatYAxisTick}
            ticks={ticks}
          />
          <Tooltip
            content={
              <ChartTooltip showColorIndicator={true} unit={item.unit} />
            }
            animationDuration={CHART_STYLES.ANIMATION.DURATION}
            animationEasing={CHART_STYLES.ANIMATION.EASING}
            cursor={false}
          />
          <Bar
            dataKey="value"
            radius={[CHART_STYLES.RADIUS.BAR, CHART_STYLES.RADIUS.BAR, 0, 0]}
            fill={CHART_STYLES.COLORS.PRIMARY}
          />
        </BarChart>
      </ResponsiveContainer>
    </BaseCard>
  );
};
